// =============================================================================
//  AjusteDeTiempos.jsx
//  EL PASO 4: DÓNDE SALTA CADA EJERCICIO
//
//  Rob, 06-10-2026: «sería como un timeline con sus punteros de cada ejercicio,
//  la intención es acomodarlo con precisión para que encaje exactamente en
//  donde tiene que responder... también muy importante ver el texto del
//  transcript para ver, oír, escuchar y modificar con precisión».
//
//  ── LAS TRES COSAS QUE TIENE QUE PODER HACER ────────────────────────────────
//  1. VER dónde cae cada pregunta respecto al vídeo entero: la línea con una
//     banda por fragmento y una burbuja numerada por ejercicio.
//  2. OÍR lo que suena justo antes, con dos entradas: desde cinco segundos
//     antes —para coger la frase entera— y desde el disparo mismo, que es lo
//     que oirá el alumno al volver de contestar.
//  3. MOVERLO fino: se arrastra la burbuja para lo gordo, las flechas ← → del
//     teclado mueven una décima (un segundo con Shift), y «Poner aquí» coge el
//     minuto en el que esté el vídeo.
//
//  Y por encima de todo, PARAR DONDE PARARÁ: el vídeo se detiene solo en cada
//  pregunta igual que le va a pasar al alumno. Mirar un número no dice si la
//  frase había terminado; verlo pararse, sí.
//
//  ── LO QUE CAMBIÓ EL 07-10-2026 ─────────────────────────────────────────────
//  Rob, usándolo con un vídeo de cinco minutos y siete fragmentos:
//
//  · «quiero invertir el orden, la letra arriba y abajo la línea del tiempo».
//    El texto es lo que se mira mientras se escucha; tenerlo debajo de la línea
//    obligaba a bajar la vista justo cuando hay que tenerla puesta en la frase.
//  · «cuando cambio de sección no trae el texto de esa parte, se queda
//    congelado en la primera de todo el vídeo». Era verdad y era grave: el
//    texto seguía al ejercicio ELEGIDO, no al vídeo. Mover la cabeza por la
//    línea no cambiaba una palabra, así que el texto no servía para lo único
//    que está ahí. Ahora sigue a dónde va el vídeo.
//  · «todo lo demás minimizado, con un clic que lo abra».
//  · «no tenemos botones de crecer el timeline para ver con más precisión,
//    como en After Effects o Premiere». Con cinco minutos en mil píxeles, una
//    décima son tres milésimas de píxel: la burbuja no se podía colocar
//    arrastrando, sólo con los botones.
//  · «si se llegan a empatar dos círculos, el siguiente debe posicionarse
//    arriba con una línea de seguimiento». Dos burbujas a dos segundos una de
//    otra se solapaban y sólo se podía coger la de encima.
// =============================================================================
import React, { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import {
    puntosDeDisparo, avisosDe, avisosDelVideo, relojFino, aDecima,
} from './tiemposDeDisparo';
import VistaDelEjercicio from './VistaDelEjercicio';

const EMOJI = {
    truefalse: '⚖️', quiz: '🎯', fill: '✏️',
    wordselect: '🔤', match: '🔗', dragdrop: '🧩',
};

//  El nombre en cristiano de cada tipo. Hace falta para el aviso de «se pidió
//  una cosa y la IA devolvió otra»: decir «se pidió truefalse» no es decírselo
//  a nadie.
const ETIQUETAS = {
    truefalse: 'Verdadero/Falso', quiz: 'Quiz', fill: 'Completar',
    wordselect: 'Vocabulario', match: 'Parejas', dragdrop: 'Arrastrar',
};

const ZOOMS = [1, 2, 4, 8, 16, 32];

//  Lo ancho que ocupa una burbuja más un poco de aire. Dos que caigan a menos
//  de esto se pisan, y hay que subir la segunda.
const ANCHO_BURBUJA = 30;
const ALTO_NIVEL = 24;

//  Un vistazo a lo que pregunta el ejercicio, para reconocerlo sin abrirlo.
function resumen(data) {
    if (!data) return '(sin generar)';
    if (data.question) return data.question;
    if (data.statement) return data.statement;
    if (data.template) return data.template;
    if (Array.isArray(data.targetWords)) return data.targetWords.join(', ');
    if (Array.isArray(data.pairs)) return data.pairs.map(p => p?.left).filter(Boolean).join(', ');
    return '(sin texto)';
}

/** Qué fragmento suena en este segundo. */
function fragmentoEn(segments, t) {
    const lista = Array.isArray(segments) ? segments : [];
    let encontrado = null;
    for (let i = 0; i < lista.length; i++) {
        const s = lista[i];
        const ini = Number(s.startTime) || 0;
        const fin = Number(s.endTime) || 0;
        if (t >= ini && t < fin) return { seg: s, indice: i };
        //  Entre dos fragmentos —o pasado el último— manda el último que ya
        //  empezó: es lo último que se ha oído.
        if (t >= ini) encontrado = { seg: s, indice: i };
    }
    return encontrado || (lista.length ? { seg: lista[0], indice: 0 } : null);
}

export default function AjusteDeTiempos({
    segments, duracion, tiempo, reproduciendo,
    seleccion, onSelecciona, onMueve, onBusca, onOye, onPlayPause,
    onSigue, pararEnDisparos, onCambiaParada, dondeParo,
}) {
    const puntos = puntosDeDisparo(segments);
    const avisos = avisosDelVideo(puntos, duracion);
    const ventanaRef = useRef(null);
    const pistaRef = useRef(null);
    const [arrastrando, setArrastrando] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [anchoPista, setAnchoPista] = useState(0);
    const [abierto, setAbierto] = useState(false);

    //  ── EL EJERCICIO ENTERO, SIN SALIR DE AQUÍ (14-10-2026) ───────────
    //  Rob: «cuando llega el disparo se detiene perfecto, pero no puedo ver las
    //  opciones del ejercicio ni la explicación... quiero que si le doy clic se
    //  haga efecto acordeón y se vea el ejercicio completo; si no le doy clic se
    //  mantiene cerrado».
    //
    //  Es un CONJUNTO y no uno solo a la vez, igual que en el paso 3: revisar es
    //  comparar dos preguntas seguidas, y cerrar la anterior al abrir la
    //  siguiente obliga a ir y volver.
    //
    //  Abrir NO es lo mismo que elegir. El vídeo se para solo en cada disparo y
    //  eso ELIGE la fila: si elegir abriera, la lista se estaría desplegando
    //  sola a cada parada. Por eso van separados, y lo cerrado es lo de partida.
    const [abiertas, setAbiertas] = useState(() => new Set());
    const alterna = useCallback((clave) => {
        setAbiertas((antes) => {
            const fuera = new Set(antes);
            if (fuera.has(clave)) fuera.delete(clave); else fuera.add(clave);
            return fuera;
        });
    }, []);

    //  Sin duración no hay escala y no se puede situar nada. Pasa durante el
    //  primer segundo, mientras YouTube arranca.
    const hayEscala = duracion > 0;
    const aPorcentaje = (s) => (hayEscala ? Math.min(100, Math.max(0, (s / duracion) * 100)) : 0);

    //  El ancho REAL en píxeles, que es lo que decide si dos burbujas se pisan.
    //  En porcentaje no se puede saber: el mismo 0,5 % son dos píxeles sin zoom
    //  y sesenta con el máximo.
    useLayoutEffect(() => {
        const mide = () => setAnchoPista(pistaRef.current?.getBoundingClientRect().width || 0);
        mide();
        const obs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(mide) : null;
        if (obs && pistaRef.current) obs.observe(pistaRef.current);
        window.addEventListener('resize', mide);
        return () => { obs?.disconnect(); window.removeEventListener('resize', mide); };
    }, [zoom]);

    const segundosDesdeX = useCallback((clientX) => {
        const caja = pistaRef.current?.getBoundingClientRect();
        if (!caja || !duracion) return 0;
        const r = (clientX - caja.left) / caja.width;
        return aDecima(Math.min(1, Math.max(0, r)) * duracion);
    }, [duracion]);

    //  ── LAS ALTURAS ───────────────────────────────────────────────────
    //  Rob: «si se llegan a empatar dos círculos, el siguiente debe
    //  posicionarse arriba con una línea de seguimiento». Se mira en PÍXELES y
    //  no en segundos, porque al acercar el zoom dejan de pisarse solas.
    const porPx = hayEscala && anchoPista ? anchoPista / duracion : 0;
    const niveles = [];
    const ocupados = [];   // el último x de cada altura
    for (const p of puntos) {
        const x = porPx ? p.disparo * porPx : 0;
        let nivel = 0;
        while (ocupados[nivel] !== undefined && x - ocupados[nivel] < ANCHO_BURBUJA) nivel += 1;
        ocupados[nivel] = x;
        niveles.push(nivel);
    }

    //  ── CLIC ES ELEGIR; ARRASTRAR ES MOVER ────────────────────────────
    //  Rob, 07-10-2026, en el editor de precisión del paso 2: «le di clic y se
    //  modificó al darle clic... si sólo intento darle clic en un punto
    //  inexacto se modifica, eso rompe con todo».
    //
    //  Aquí pasaba lo mismo en pequeño: la burbuja se movía con el primer
    //  `pointermove`, y una mano que tiembla al pulsar ya genera uno. Tres
    //  píxeles: más de lo que tiembla una mano, menos de lo que mueve quien
    //  quiere mover.
    const UMBRAL_PX = 3;
    const xAlPulsar = useRef(0);
    const seMovio = useRef(false);

    const empiezaArrastre = (e, punto) => {
        if (!hayEscala) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        xAlPulsar.current = e.clientX;
        seMovio.current = false;
        setArrastrando(punto.clave);
        onSelecciona(punto.clave);
    };

    const arrastra = (e, punto) => {
        if (arrastrando !== punto.clave) return;
        if (!seMovio.current) {
            if (Math.abs(e.clientX - xAlPulsar.current) < UMBRAL_PX) return;
            seMovio.current = true;
        }
        onMueve(punto.segId, punto.exId, segundosDesdeX(e.clientX));
    };

    const sueltaArrastre = (e) => {
        if (!arrastrando) return;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        setArrastrando(null);
        seMovio.current = false;
    };

    const elegido = puntos.find(p => p.clave === seleccion) || puntos[0] || null;

    //  Con el zoom puesto la línea es más ancha que la ventana. Se sigue la
    //  cabeza sola, porque si no, acercar el zoom es perder el vídeo de vista.
    useEffect(() => {
        const v = ventanaRef.current;
        if (!v || zoom === 1 || !hayEscala) return;
        const x = (tiempo / duracion) * v.scrollWidth;
        if (x < v.scrollLeft + 60 || x > v.scrollLeft + v.clientWidth - 60) {
            v.scrollTo({ left: Math.max(0, x - v.clientWidth / 2), behavior: 'smooth' });
        }
    }, [tiempo, zoom, duracion, hayEscala]);

    //  Y al elegir otra burbuja, se va a verla.
    useEffect(() => {
        const v = ventanaRef.current;
        if (!v || zoom === 1 || !hayEscala || !elegido) return;
        const x = (elegido.disparo / duracion) * v.scrollWidth;
        if (x < v.scrollLeft + 40 || x > v.scrollLeft + v.clientWidth - 40) {
            v.scrollTo({ left: Math.max(0, x - v.clientWidth / 2), behavior: 'smooth' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seleccion, zoom]);

    //  ── EL TEXTO SIGUE AL VÍDEO ───────────────────────────────────────
    //  No al ejercicio elegido. Ése era el fallo: mover la cabeza por la línea
    //  no cambiaba una palabra del texto, así que no servía para lo único que
    //  está ahí — comprobar si la frase había terminado.
    const sonando = fragmentoEn(segments, tiempo);
    const delElegido = elegido
        ? (segments || []).findIndex(s => s.id === elegido.segId)
        : -1;
    const alturaPista = 26 + (ocupados.length || 1) * ALTO_NIVEL;

    return (
        <div className="at-root">
            {/* ── EL TEXTO, ARRIBA ─────────────────────────────────────── */}
            <div className="at-letra">
                <div className="at-letra-cab">
                    <span className="at-letra-tit">
                        Lo que se oye ahora · fragmento #{(sonando?.indice ?? 0) + 1}
                        {sonando && (
                            <span className="at-letra-rango">
                                {relojFino(sonando.seg.startTime)} → {relojFino(sonando.seg.endTime)}
                            </span>
                        )}
                    </span>
                    {/* El ejercicio elegido puede ser de otro fragmento: se
                        dice, porque si no parece que el texto está mal. */}
                    {elegido && delElegido >= 0 && delElegido !== sonando?.indice && (
                        <button
                            className="at-letra-salta"
                            onClick={() => onBusca(Math.max(0, (segments[delElegido]?.startTime || 0)))}
                        >
                            El ejercicio {elegido.orden} es del #{delElegido + 1} → ir
                        </button>
                    )}
                </div>
                <p className="at-letra-texto">
                    {sonando?.seg?.text || '(este fragmento no tiene texto)'}
                </p>
            </div>

            {/* ── LA LÍNEA DEL VÍDEO ───────────────────────────────────── */}
            <div className="at-linea-caja">
                <div className="at-linea-cabecera">
                    <button className="at-play" onClick={onPlayPause}
                            title={reproduciendo ? 'Pausar' : 'Reproducir'}>
                        {reproduciendo ? '⏸' : '▶'}
                    </button>
                    <span className="at-reloj-grande">{relojFino(tiempo)}</span>
                    <span className="at-reloj-fin">de {hayEscala ? relojFino(duracion) : '--:--'}</span>

                    <label className="at-interruptor" title="El vídeo se detiene solo en cada pregunta, igual que le pasará al alumno">
                        <input
                            type="checkbox"
                            checked={!!pararEnDisparos}
                            onChange={(e) => onCambiaParada(e.target.checked)}
                        />
                        Parar en los disparos
                    </label>

                    {/* ── EL ZOOM ──────────────────────────────────────────
                        Con cinco minutos en mil píxeles, una décima son tres
                        milésimas de píxel: la burbuja no se puede colocar
                        arrastrando. Acercando, cada décima gana sitio. */}
                    <div className="at-zoom">
                        <button
                            className="at-zoom-btn"
                            onClick={() => setZoom(z => ZOOMS[Math.max(0, ZOOMS.indexOf(z) - 1)])}
                            disabled={zoom === ZOOMS[0]}
                            title="Alejar"
                        >−</button>
                        <span className="at-zoom-val" title="Cuánto se ve ampliada la línea">×{zoom}</span>
                        <button
                            className="at-zoom-btn"
                            onClick={() => setZoom(z => ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(z) + 1)])}
                            disabled={zoom === ZOOMS[ZOOMS.length - 1]}
                            title="Acercar"
                        >+</button>
                    </div>

                    <span className="at-pista-ayuda">
                        <kbd>←</kbd><kbd>→</kbd> una décima · <kbd>Shift</kbd> un segundo
                        · <kbd>espacio</kbd> play/pausa · <kbd>enter</kbd> continuar
                    </span>
                </div>

                <div className="at-pista-ventana" ref={ventanaRef}>
                    <div
                        className="at-pista"
                        ref={pistaRef}
                        style={{ width: `${zoom * 100}%`, height: alturaPista }}
                        onClick={(e) => {
                            //  Sólo si no se venía de arrastrar: soltar una
                            //  burbuja encima dispararía también el salto.
                            if (arrastrando) return;
                            onBusca(segundosDesdeX(e.clientX));
                        }}
                    >
                        {hayEscala && (segments || []).map((seg, i) => {
                            const x = aPorcentaje(Number(seg.startTime) || 0);
                            const w = aPorcentaje(Number(seg.endTime) || 0) - x;
                            return (
                                <div key={seg.id}
                                     className={`at-banda ${i % 2 ? 'impar' : ''} `
                                         + `${i === sonando?.indice ? 'sonando' : ''}`}
                                     style={{ left: `${x}%`, width: `${Math.max(0.2, w)}%` }}
                                     title={`Fragmento #${i + 1}`}>
                                    <span className="at-banda-num">{i + 1}</span>
                                </div>
                            );
                        })}

                        <div className="at-recorrido" style={{ width: `${aPorcentaje(tiempo)}%` }} />
                        <div className="at-cabeza" style={{ left: `${aPorcentaje(tiempo)}%` }} />

                        {hayEscala && puntos.map((p, i) => {
                            const nivel = niveles[i] || 0;
                            return (
                                <React.Fragment key={p.clave}>
                                    {/* La línea de seguimiento: cuando una
                                        burbuja sube para no pisarse con la de
                                        al lado, esto dice de qué punto de la
                                        línea sale. Sin ella, una burbuja a dos
                                        alturas no se sabe a qué segundo cae. */}
                                    {nivel > 0 && (
                                        <span className={`at-guia ${seleccion === p.clave ? 'elegida' : ''}`}
                                              style={{ left: `${aPorcentaje(p.disparo)}%`,
                                                       height: nivel * ALTO_NIVEL + 10 }} />
                                    )}
                                    <button
                                        className={`at-burbuja ${seleccion === p.clave ? 'elegida' : ''} `
                                            + `${arrastrando === p.clave && seMovio.current ? 'moviendo' : ''} `
                                            + `${p.listo ? '' : 'sinhacer'}`}
                                        style={{ left: `${aPorcentaje(p.disparo)}%`,
                                                 bottom: `${6 + nivel * ALTO_NIVEL}px` }}
                                        onPointerDown={(e) => empiezaArrastre(e, p)}
                                        onPointerMove={(e) => arrastra(e, p)}
                                        onPointerUp={sueltaArrastre}
                                        onPointerCancel={sueltaArrastre}
                                        onClick={(e) => { e.stopPropagation(); onSelecciona(p.clave); }}
                                        title={`Ejercicio ${p.orden} · ${relojFino(p.disparo)}`}
                                    >
                                        {p.orden}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── DÓNDE SE HA PARADO ───────────────────────────────────── */}
            {dondeParo && (
                <div className="at-paro">
                    <span className="at-paro-num">{dondeParo.orden}</span>
                    <span className="at-paro-texto">
                        El vídeo se paró aquí, en <strong>{relojFino(dondeParo.disparo)}</strong>.
                        Mira la frase de arriba: ¿había terminado?
                    </span>
                    <button className="at-accion principal" onClick={onSigue}>
                        Continuar ▶ <kbd>enter</kbd>
                    </button>
                </div>
            )}

            {/* ── LO QUE ESTÁ MAL COLOCADO ─────────────────────────────── */}
            {avisos.length > 0 && (
                <div className="at-avisos">
                    {avisos.map((a, i) => (
                        <button key={i} className={`at-aviso ${a.tipo}`}
                                onClick={() => onSelecciona(a.clave)}>
                            <strong>#{a.orden}</strong> {a.texto}
                        </button>
                    ))}
                </div>
            )}

            {/* ── EL EJERCICIO ELEGIDO ─────────────────────────────────────
                De una línea. Lo que hace falta a cada décima son los mandos; la
                pregunta y el resto se abren con un clic cuando se quieren. */}
            {elegido && (
                <div className={`at-detalle ${abierto ? 'abierto' : ''}`}>
                    <div className="at-mandos">
                        <button className="at-abrir" onClick={() => setAbierto(a => !a)}
                                title={abierto ? 'Minimizar' : 'Ver el ejercicio entero'}>
                            {abierto ? '▾' : '▸'}
                        </button>
                        <span className="at-num">{elegido.orden}</span>
                        <span className="at-tipo">{EMOJI[elegido.tipo] || '❓'}</span>
                        <div className="at-tiempo-grande">{relojFino(elegido.disparo)}</div>
                        <div className="at-botones-finos">
                            {[-1, -0.1, 0.1, 1].map(d => (
                                <button
                                    key={d}
                                    className="at-fino"
                                    onClick={() => onMueve(elegido.segId, elegido.exId,
                                                           elegido.disparo + d)}
                                >
                                    {d > 0 ? `+${d}` : d}s
                                </button>
                            ))}
                        </div>
                        <button
                            className="at-accion"
                            onClick={() => onMueve(elegido.segId, elegido.exId, tiempo)}
                            title="Poner el disparo en el minuto en el que está el vídeo"
                        >
                            ⏱ Poner aquí ({relojFino(tiempo)})
                        </button>
                        <button
                            className="at-accion principal"
                            onClick={() => onOye(Math.max(0, elegido.disparo - 5))}
                            title="Rebobina cinco segundos antes del disparo y reproduce"
                        >
                            🔊 Oír desde −5 s
                        </button>
                        <button
                            className="at-accion"
                            onClick={() => onOye(elegido.disparo)}
                            title="Reproduce desde el disparo mismo: lo que oirá el alumno al volver"
                        >
                            ▶ Desde el disparo
                        </button>
                    </div>

                    {/* ── EL EJERCICIO ENTERO, NO SU PRIMERA LÍNEA ─────────
                        Esto enseñaba sólo la pregunta. Rob, 14-10-2026: «no
                        puedo ver las opciones de ejercicios como quiz o la
                        explicación de ese ejercicio». Con la pregunta sola no se
                        puede juzgar nada: la trampa de un ejercicio malo está en
                        los distractores y en la explicación. Es la misma vista
                        del paso 3, que ya enseña la respuesta correcta marcada y
                        no deja tocar nada. */}
                    {abierto && (
                        <div className="at-detalle-abierto">
                            <div className="at-detalle-cabecera">
                                <span className="at-tipo">
                                    {EMOJI[elegido.tipo] || '❓'} {ETIQUETAS[elegido.tipo] || elegido.tipo}
                                </span>
                                <span className="at-origen">
                                    fragmento #{elegido.segmento} ·
                                    {' '}{relojFino(elegido.inicio)} → {relojFino(elegido.fin)}
                                </span>
                            </div>
                            <VistaDelEjercicio
                                data={elegido.data}
                                tipoPedido={elegido.tipo}
                                etiquetas={ETIQUETAS}
                            />
                        </div>
                    )}

                    {avisosDe(elegido, duracion).map((a, i) => (
                        <div key={i} className={`at-aviso-detalle ${a.tipo}`}>{a.texto}</div>
                    ))}
                </div>
            )}

            {/* ── TODOS, EN EL ORDEN EN QUE LOS VERÁ EL ALUMNO ─────────── */}
            <div className="at-lista">
                {puntos.length === 0 && (
                    <div className="at-vacio">
                        Este vídeo todavía no tiene ejercicios. Vuelve al paso 3.
                    </div>
                )}
                {puntos.map(p => {
                    const desplegada = abiertas.has(p.clave);
                    return (
                        <div
                            key={p.clave}
                            className={`at-fila-caja ${desplegada ? 'desplegada' : ''}`}
                            data-prueba="at-fila-caja"
                        >
                            <button
                                className={`at-fila ${seleccion === p.clave ? 'elegida' : ''}`}
                                data-prueba={`at-fila-${p.orden}`}
                                aria-expanded={desplegada}
                                //  Un clic hace las dos cosas: elige la fila —que es
                                //  lo que mandan los mandos de arriba— y abre lo que
                                //  hay dentro. Pedir dos clics para ver una pregunta,
                                //  teniendo que acertar con el triangulito, es lo que
                                //  hacía que no se mirara nunca.
                                onClick={() => { onSelecciona(p.clave); alterna(p.clave); }}
                                title={desplegada
                                    ? 'Cerrar el ejercicio'
                                    : 'Ver el ejercicio entero: opciones, respuesta y explicación'}
                            >
                                <span className="at-fila-flecha">{desplegada ? '▾' : '▸'}</span>
                                <span className="at-num pequeno">{p.orden}</span>
                                <span className="at-fila-tiempo">{relojFino(p.disparo)}</span>
                                <span className="at-fila-tipo">{EMOJI[p.tipo] || '❓'}</span>
                                <span className="at-fila-texto">{resumen(p.data)}</span>
                                <span className="at-fila-seg">#{p.segmento}</span>
                            </button>
                            {desplegada && (
                                <div className="at-fila-ejercicio" data-prueba="at-fila-abierta">
                                    {/* El fragmento del que salió, encima de todo: sin
                                        eso no se puede juzgar si la pregunta cae donde
                                        tiene que caer, que es a lo que se viene aquí. */}
                                    <div className="at-fila-origen">
                                        {ETIQUETAS[p.tipo] || p.tipo} · fragmento #{p.segmento} ·
                                        {' '}{relojFino(p.inicio)} → {relojFino(p.fin)}
                                    </div>
                                    {p.texto && <p className="at-fila-transcript">«{p.texto}»</p>}
                                    <VistaDelEjercicio
                                        data={p.data}
                                        tipoPedido={p.tipo}
                                        etiquetas={ETIQUETAS}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
